import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { 
  getReportDefinition, 
  getReportResultsV2, 
  getReportSummaryV2, 
  getReportStalenessStatus 
} from "../../../services/reportService";
import { REPORT_KEYS, isValidReportId } from "../queryKeys";
import GetReportResultsRequest from "../../../models/serviceModels/GetReportResultsRequest";
import PagedData from "../../../models/serviceModels/PagedData";

export const isNotFoundError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

const retryReportQuery = (failureCount: number, error: Error): boolean =>
  !isNotFoundError(error) && failureCount < 3;

export function useReportDefinitionQuery(reportId: string) {
  return useQuery({
    queryKey: REPORT_KEYS.definition(reportId),
    queryFn: ({ signal }) => getReportDefinition(reportId, signal),
    enabled: isValidReportId(reportId),
    retry: retryReportQuery,
  });
}

export function useReportResultsQuery<T>(request: GetReportResultsRequest) {
  return useQuery<PagedData<T>>({
    queryKey: REPORT_KEYS.results(request.reportId, request),
    queryFn: () => getReportResultsV2<T>(request),
    enabled: isValidReportId(request.reportId),
  });
}

export function useReportSummaryQuery<T>(reportId: string) {
  return useQuery<T>({
    queryKey: REPORT_KEYS.summary(reportId),
    queryFn: ({ signal }) => getReportSummaryV2<T>(reportId, signal),
    enabled: isValidReportId(reportId),
    retry: retryReportQuery,
  });
}

export function useReportStalenessQuery(reportId: string, enabled: boolean) {
  return useQuery<{ isStale: boolean; parentReportId?: string }>({
    queryKey: REPORT_KEYS.staleness(reportId),
    queryFn: () => getReportStalenessStatus(reportId),
    enabled: isValidReportId(reportId) && enabled,
    staleTime: 0,
    gcTime: 0,
  });
}
